import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getCheckoutOptions, formatCheckout } from './utils/checkouts';
import './CheckoutDuel.css';

// ─────────────────────────────────────────────
// CONSTANTES
// ─────────────────────────────────────────────
const RANGES = [
  { label: '51 – 70', min: 51, max: 70 },
  { label: '71 – 90', min: 71, max: 90 },
];

// ─────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ─────────────────────────────────────────────
const CheckoutDuel = ({ onBack }) => {
  // ── Fase ──
  const [phase, setPhase] = useState('setup'); // 'setup' | 'playing' | 'finished'

  // ── Configuración ──
  const [gameMode, setGameMode] = useState('duel'); // 'solo' | 'duel'
  const [rangeIndex, setRangeIndex] = useState(0);
  const [name1, setName1] = useState('');
  const [name2, setName2] = useState('');

  // ── Estado de partida ──
  const [activeRange, setActiveRange] = useState(RANGES[0]);
  const [currentTarget, setCurrentTarget] = useState(RANGES[0].min);
  const [activePlayer, setActivePlayer] = useState(1);
  const [p1, setP1] = useState({ name: 'Jugador 1', score: 0, attempts: 0 });
  const [p2, setP2] = useState({ name: 'Jugador 2', score: 0, attempts: 0 });

  // ── Animación de transición ──
  const [cardVisible, setCardVisible] = useState(true);
  const transitionRef = useRef(null);

  // Limpieza al desmontar
  useEffect(() => {
    return () => {
      if (transitionRef.current) clearTimeout(transitionRef.current);
    };
  }, []);

  // ─────────────────────────────────────────────
  // LÓGICA
  // ─────────────────────────────────────────────

  const handleStart = useCallback(() => {
    const range = RANGES[rangeIndex];
    const finalName1 = name1.trim() || 'Jugador 1';
    const finalName2 = name2.trim() || 'Jugador 2';
    setP1({ name: finalName1, score: 0, attempts: 0 });
    setP2({ name: finalName2, score: 0, attempts: 0 });
    setCurrentTarget(range.min);
    setActiveRange(range);
    setActivePlayer(1);
    setPhase('playing');
  }, [rangeIndex, name1, name2]);

  const animateTransition = useCallback((onComplete) => {
    setCardVisible(false);
    transitionRef.current = setTimeout(() => {
      onComplete();
      setCardVisible(true);
    }, 160);
  }, []);

  const handleAnswer = useCallback((hit) => {
    // Actualizar estado del jugador activo
    if (activePlayer === 1) {
      setP1(prev => ({
        ...prev,
        score: hit ? prev.score + 1 : prev.score,
        attempts: prev.attempts + 1,
      }));
    } else {
      setP2(prev => ({
        ...prev,
        score: hit ? prev.score + 1 : prev.score,
        attempts: prev.attempts + 1,
      }));
    }

    // En duelo: avanza solo si acierta. En solo: siempre avanza.
    const advance = hit || gameMode === 'solo';
    const nextTarget = advance ? currentTarget + 1 : currentTarget;

    if (nextTarget > activeRange.max) {
      setTimeout(() => setPhase('finished'), 100);
    } else {
      animateTransition(() => {
        setCurrentTarget(nextTarget);
        if (gameMode === 'duel') {
          setActivePlayer(prev => (prev === 1 ? 2 : 1));
        }
      });
    }
  }, [activePlayer, gameMode, currentTarget, activeRange.max, animateTransition]);

  const handleRestart = useCallback(() => {
    setName1(p1.name !== 'Jugador 1' ? p1.name : '');
    setName2(p2.name !== 'Jugador 2' ? p2.name : '');
    setPhase('setup');
  }, [p1.name, p2.name]);

  // ─────────────────────────────────────────────
  // DATOS DE RENDER
  // ─────────────────────────────────────────────
  const TOTAL_TARGETS = activeRange.max - activeRange.min + 1;
  const progress = currentTarget - activeRange.min;
  const progressPct = Math.min((progress / TOTAL_TARGETS) * 100, 100);
  const checkoutSuggestion = formatCheckout(getCheckoutOptions(currentTarget)?.[0] ?? '');
  const activeState = activePlayer === 1 ? p1 : p2;
  const isP1Active = activePlayer === 1;

  const hitPct = (p) => p.attempts > 0 ? Math.round((p.score / p.attempts) * 100) : 0;

  const rulesText = gameMode === 'solo'
    ? ['🏹 Intentas cada cierre una vez', '✅ Acierto → 1 punto, avanza', '❌ Fallo → 0 puntos, avanza igualmente', '📊 Al final ves tu % de acierto']
    : ['🏹 Alternáis tiradas al mismo cierre', '✅ Acierto → 1 punto y el cierre avanza', '❌ Fallo → 0 puntos, el rival tira al mismo', '🏆 Gana quien consiga más cierres'];

  // ─────────────────────────────────────────────
  // RENDER – SETUP
  // ─────────────────────────────────────────────
  if (phase === 'setup') {
    return (
      <div className="cd-container">
        <div className="cd-setup">
          {/* Columna izquierda (en pantallas anchas) */}
          <div className="cd-setup-col-left">
            <button className="cd-back-btn" onClick={onBack}>← Volver</button>

            <h1 className="cd-setup-title">🎯 Cierres</h1>

            {/* Selector de modo */}
            <p className="cd-section-label">Modo de juego</p>
            <div className="cd-segment-row">
              {['solo', 'duel'].map(m => (
                <button
                  key={m}
                  className={`cd-segment-btn${gameMode === m ? ' active' : ''}`}
                  onClick={() => setGameMode(m)}
                >
                  {m === 'solo' ? '👤 Individual' : '⚔️ Duelo'}
                </button>
              ))}
            </div>

            {/* Selector de rango */}
            <p className="cd-section-label">Rango de cierres</p>
            <div className="cd-segment-row">
              {RANGES.map((r, i) => (
                <button
                  key={r.label}
                  className={`cd-segment-btn${rangeIndex === i ? ' active' : ''}`}
                  onClick={() => setRangeIndex(i)}
                >
                  {r.label}
                </button>
              ))}
            </div>

            {/* Reglas */}
            <ul className="cd-rules">
              {rulesText.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          </div>

          {/* Columna derecha (en pantallas anchas) */}
          <div className="cd-setup-col-right">
          {/* Nombres */}
          <div className="cd-setup-card">
            <label className="cd-input-label">
              {gameMode === 'solo' ? 'Tu nombre' : 'Jugador 1'}
            </label>
            <input
              className="cd-input"
              type="text"
              value={name1}
              onChange={e => setName1(e.target.value)}
              placeholder={gameMode === 'solo' ? 'Tu nombre' : 'Jugador 1'}
              maxLength={20}
              onKeyDown={e => e.key === 'Enter' && gameMode === 'solo' && handleStart()}
              autoComplete="off"
            />

            {gameMode === 'duel' && (
              <>
                <label className="cd-input-label" style={{ marginTop: 18 }}>Jugador 2</label>
                <input
                  className="cd-input"
                  type="text"
                  value={name2}
                  onChange={e => setName2(e.target.value)}
                  placeholder="Jugador 2"
                  maxLength={20}
                  onKeyDown={e => e.key === 'Enter' && handleStart()}
                  autoComplete="off"
                />
              </>
            )}
          </div>

          <button className="cd-start-btn" onClick={handleStart}>
            {gameMode === 'solo' ? '🏹 Empezar' : '🏹 Iniciar Duelo'}
          </button>
          </div>{/* end col-right */}
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────
  // RENDER – PLAYING
  // ─────────────────────────────────────────────
  return (
    <div className="cd-container">
      <div className="cd-playing">

      {/* ── MARCADOR ── */}
      {gameMode === 'duel' ? (
        <div className="cd-scoreboard">
          <div className={`cd-player-box${isP1Active ? ' active' : ''}`}>
            {isP1Active && <span className="cd-active-dot" />}
            <div className="cd-player-name">{p1.name}</div>
            <div className="cd-player-score">{p1.score}</div>
            <div className="cd-player-pct">
              {p1.attempts > 0 ? `${hitPct(p1)}% acierto` : '—'}
            </div>
          </div>

          <div className="cd-score-divider">
            <span className="cd-vs">VS</span>
            <span className="cd-progress-label">{progress + 1}/{TOTAL_TARGETS}</span>
          </div>

          <div className={`cd-player-box${!isP1Active ? ' active' : ''}`}>
            {!isP1Active && <span className="cd-active-dot" />}
            <div className="cd-player-name">{p2.name}</div>
            <div className="cd-player-score">{p2.score}</div>
            <div className="cd-player-pct">
              {p2.attempts > 0 ? `${hitPct(p2)}% acierto` : '—'}
            </div>
          </div>
        </div>
      ) : (
        <div className="cd-scoreboard cd-scoreboard-solo">
          <div className="cd-player-box active">
            <div className="cd-player-name">{p1.name}</div>
            <div className="cd-player-score">{p1.score}</div>
            <div className="cd-player-pct">
              {p1.attempts > 0 ? `${hitPct(p1)}% acierto` : '—'}
            </div>
          </div>
          <span className="cd-progress-label">{progress + 1} / {TOTAL_TARGETS}</span>
        </div>
      )}

      {/* ── BARRA DE PROGRESO ── */}
      <div className="cd-progress-bar">
        <div className="cd-progress-fill" style={{ width: `${progressPct}%` }} />
      </div>

      {/* ── CARD CENTRAL ── */}
      <div className={`cd-game-card${cardVisible ? '' : ' cd-fade-out'}${isP1Active ? ' player1' : ' player2'}`}>
        {gameMode === 'duel' && (
          <>
            <div className="cd-turn-label">Turno de</div>
            <div className="cd-active-name">{activeState.name}</div>
          </>
        )}
        <div className="cd-target-circle">
          <span className="cd-target-number">{currentTarget}</span>
        </div>
        {checkoutSuggestion && (
          <div className="cd-checkout-hint">{checkoutSuggestion}</div>
        )}
        <div className="cd-question">¿Lo conseguiste?</div>
      </div>

      {/* ── BOTONES ── */}
      <div className="cd-answer-btns">
        <button className="cd-btn-yes" onClick={() => handleAnswer(true)}>
          ✅ SÍ
        </button>
        <button className="cd-btn-no" onClick={() => handleAnswer(false)}>
          ❌ NO
        </button>
      </div>

      {/* ══════════════════════════════════════════
           MODAL DE FIN DE PARTIDA
      ══════════════════════════════════════════ */}
      {phase === 'finished' && (
        <div className="cd-modal-overlay">
          <div className="cd-modal">
            <h2 className="cd-modal-title">
              {gameMode === 'solo' ? '📊 Entrenamiento Terminado' : '🏆 Duelo Terminado'}
            </h2>

            {gameMode === 'duel' && (
              <p className="cd-modal-winner">
                {p1.score > p2.score
                  ? `🥇 ¡Gana ${p1.name}!`
                  : p2.score > p1.score
                    ? `🥇 ¡Gana ${p2.name}!`
                    : '🤝 ¡Empate!'}
              </p>
            )}

            {(gameMode === 'solo' ? [p1] : [p1, p2]).map((p, i) => {
              const hp = hitPct(p);
              const isWinner = gameMode === 'duel'
                ? (i === 0 ? p1.score > p2.score : p2.score > p1.score)
                : false;

              return (
                <div key={i} className={`cd-stat-row${isWinner ? ' winner' : ''}`}>
                  <div className="cd-stat-name">
                    {isWinner && <span>👑 </span>}
                    {p.name}
                  </div>
                  <div className="cd-stat-grid">
                    <div className="cd-stat-item">
                      <span className="cd-stat-value">{p.score}</span>
                      <span className="cd-stat-label">puntos</span>
                    </div>
                    <div className="cd-stat-item">
                      <span className="cd-stat-value cd-hit">{hp}%</span>
                      <span className="cd-stat-label">aciertos</span>
                    </div>
                    <div className="cd-stat-item">
                      <span className="cd-stat-value cd-miss">{100 - hp}%</span>
                      <span className="cd-stat-label">fallos</span>
                    </div>
                    <div className="cd-stat-item">
                      <span className="cd-stat-value">{p.attempts}</span>
                      <span className="cd-stat-label">intentos</span>
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="cd-modal-btns">
              <button className="cd-modal-btn-secondary" onClick={handleRestart}>
                🔄 Otra partida
              </button>
              <button className="cd-modal-btn-primary" onClick={onBack}>
                Salir
              </button>
            </div>
          </div>
        </div>
      )}
      </div>{/* end cd-playing */}
    </div>
  );
};

export default CheckoutDuel;
